import Coupon from '@alltrails/core/types/Coupon';
import type { Plan } from '@alltrails/types/Plan';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import { getPriceTotal } from '@alltrails/payment/utils/plan';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';
import getFreeTrialDurationPricing from './getFreeTrialDurationPricing';

interface Params {
  currencyCode: CurrencyCode;
  plan: Plan;
  isMobileSizedScreen: boolean;
  isPromoEligible?: boolean;
  coupon?: Coupon;
  subscriptionUser: SubscriptionUser | null;
}

const getPricingText = ({ plan, currencyCode, isPromoEligible, coupon, isMobileSizedScreen, subscriptionUser }: Params) => {
  if (coupon?.type === 'FreeTrialCoupon') {
    return getFreeTrialDurationPricing(plan, currencyCode, coupon);
  }

  if (coupon?.type === 'SubscriptionCoupon') {
    return (
      <FormattedMessage
        defaultMessage="{promoAnnualCost} for 1 year, then {annualCost}/year"
        values={{
          promoAnnualCost: <FormattedNumber currency={currencyCode} style="currency" value={getPriceTotal(plan, coupon)} />,
          annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />
        }}
      />
    );
  }

  if (isPromoEligible) {
    if (isMobileSizedScreen) {
      return (
        <FormattedMessage
          defaultMessage="7 days free, then {annualCost}/year ({monthlyCost}/month)"
          values={{
            annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
            monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
          }}
        />
      );
    }

    return (
      <FormattedMessage
        defaultMessage="7 days free, then {annualCost}/year (that’s {monthlyCost}/month)"
        values={{
          annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
          monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
        }}
      />
    );
  }

  // feels little backwards
  if (subscriptionUser?.canUpgradeExistingSubscription && !subscriptionUser?.subscription?.trial) {
    return (
      <FormattedMessage
        defaultMessage="{annualCost}/year, and we’ll <b>refund</b> the rest of your current plan"
        values={{
          annualCost: (
            <b>
              <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />
            </b>
          ),
          b: chunks => <b>{chunks}</b>
        }}
      />
    );
  }

  return (
    <FormattedMessage
      defaultMessage="{annualCost}/year (that’s {monthlyCost}/month)" //
      values={{
        annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
        monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
      }}
    />
  );
};

export default getPricingText;
